import {
  all,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  FIELDTEMPLATE_TYPE,
  FIELD_TYPE,
  HEARINGTYPE_TYPE,
} from '../../constants/api';
import {
  createFieldTemplateInitialize,
  deleteFieldTemplateInitialize,
  getAllHearingsError,
  getAllHearingsSuccess,
  getAllHearingTypesInitialize,
  getPaginatedHearingsError,
  getPaginatedHearingsSuccess,
  updateFieldTemplateInitialize,
} from '../actions/api';
import { fieldTemplateByIdSelector } from '../apiFeatures/fieldTemplateSlice';
import { isFetchingHearingsSelector } from '../apiFeatures/hearingSlice';
import { parseApiError } from '../globalMessageSlice';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const loadFieldTemplateAdminPage = createAction(
  'page/fieldTemplateAdministrationPage/loadFieldTemplateAdminPage',
);
const setIsPanelOpen = createAction(
  'page/fieldTemplateAdministrationPage/setIsPanelOpen',
);
const setFieldTemplateAttribute = createAction(
  'page/fieldTemplateAdministrationPage/setFieldTemplateAttribute',
);
const saveFieldTemplate = createAction(
  'page/fieldTemplateAdministrationPage/saveFieldTemplate',
);
const setFieldTemplateBeingUpdated = createAction(
  'page/fieldTemplateAdministrationPage/setFieldTemplateBeingUpdated',
);
const updateModelInState = createAction(
  'page/fieldTemplateAdministrationPage/updateModelInState',
);
const setFieldTemplateModel = createAction(
  'page/fieldTemplateAdministrationPage/setFieldTemplateModel',
);
const deleteFieldTemplate = createAction(
  'page/fieldTemplateAdministrationPage/deleteFieldTemplate',
);
// ---------- REDUCERS -----------
const fieldTemplateAdministrationPageReducer = createReducer(
  {
    isPanelOpen: false,
    fieldTemplateBeingUpdated: false,
    model: null,
  },
  (builder) => {
    builder
      .addCase(setIsPanelOpen, (state, action) => {
        state.isPanelOpen = action.payload;
      })
      .addCase(setFieldTemplateAttribute, (state, action) => {
        const { value, attributeName } = action.payload;
        state.model.attributes[attributeName] = value;
      })
      .addCase(setFieldTemplateBeingUpdated, (state, action) => {
        state.fieldTemplateBeingUpdated = action.payload;
      })
      .addCase(updateModelInState, (state, action) => {
        state.model = action.payload;
      });
  },
);
// ----------- Sagas -------------
export function* fieldTemplateAdministrationPageSaga() {
  yield all([
    fork(pageLoadSaga),
    fork(saveFieldTemplateSaga),
    fork(setFieldTemplateModelSaga),
    fork(deleteFieldTemplateSaga),
  ]);
}

function* saveFieldTemplateSaga() {
  yield takeLatest(saveFieldTemplate.type, function* () {
    yield put(setFieldTemplateBeingUpdated(true));

    const fieldTemplateModel = yield select(fieldTemplateModelSelector);
    const hearingTypeId = getRelId(fieldTemplateModel, 'hearingType');
    const fieldTemplateId = getEntityId(fieldTemplateModel);

    let saveFieldTemplateInitAction = null;
    if (fieldTemplateId) {
      saveFieldTemplateInitAction = yield put(
        updateFieldTemplateInitialize({
          hearingTypeId: hearingTypeId,
          fieldTemplateId: fieldTemplateId,
          changes: fieldTemplateModel,
        }),
      );
    } else {
      saveFieldTemplateInitAction = yield put(
        createFieldTemplateInitialize({
          hearingTypeId: hearingTypeId,
          model: { data: fieldTemplateModel },
        }),
      );
    }

    const saveFieldTemplateResult = yield awaitApiCall(
      saveFieldTemplateInitAction,
    );

    if (saveFieldTemplateResult.success) {
      yield put(setIsPanelOpen(false));
    }
    if (saveFieldTemplateResult.error) {
      yield put(parseApiError(saveFieldTemplateResult.error.payload));
    }
    yield put(setFieldTemplateBeingUpdated(false));
  });
}

function* setFieldTemplateModelSaga() {
  yield takeEvery(setFieldTemplateModel.type, function* (action) {
    const { fieldId, hearingTypeId, fieldTemplateId } = action.payload;

    if (fieldTemplateId) {
      const fieldTemplateModel = yield select(
        fieldTemplateByIdSelector(fieldTemplateId),
      );
      yield put(updateModelInState(fieldTemplateModel));
    } else {
      const fieldTemplateModel = makeEmptyModel(fieldId, hearingTypeId);
      yield put(updateModelInState(fieldTemplateModel));
    }
  });
}

function* deleteFieldTemplateSaga() {
  yield takeLatest(deleteFieldTemplate.type, function* (action) {
    yield put(setFieldTemplateBeingUpdated(true));
    const { hearingTypeId, fieldTemplateId } = action.payload;

    const deleteFieldTemplateInitAction = yield put(
      deleteFieldTemplateInitialize({
        hearingTypeId: hearingTypeId,
        fieldTemplateId: fieldTemplateId,
      }),
    );

    const deleteFieldTemplateResult = yield awaitApiCall(
      deleteFieldTemplateInitAction,
    );

    if (deleteFieldTemplateResult.error) {
      yield put(parseApiError(deleteFieldTemplateResult.error.payload));
    }
    yield put(setFieldTemplateBeingUpdated(false));
  });
}
function* pageLoadSaga() {
  yield takeLatest(loadFieldTemplateAdminPage.type, function* () {
    const isFetchingHearings = yield select(isFetchingHearingsSelector);

    if (isFetchingHearings) {
      yield race([
        take(getAllHearingsSuccess.type),
        take(getAllHearingsError.type),
        take(getPaginatedHearingsError.type),
        take(getPaginatedHearingsSuccess.type),
      ]);
    }

    yield put(getAllHearingTypesInitialize());
  });
}
// ----------- Selectors -----------

const fieldTemplateAdministrationState = (state) =>
  state.page.fieldTemplateAdministration;

const isPanelOpenSelector = createSelector(
  fieldTemplateAdministrationState,
  (state) => state.isPanelOpen,
);

const fieldTemplateModelSelector = createSelector(
  fieldTemplateAdministrationState,
  (state) => state.model,
);

const fieldTemplateBeingUpdatedSelector = createSelector(
  fieldTemplateAdministrationState,
  (state) => state.fieldTemplateBeingUpdated,
);

const fieldTemplateModelHasIdSelector = createSelector(
  [fieldTemplateModelSelector],
  (fieldTemplateModel) => !!getEntityId(fieldTemplateModel),
);

export {
  loadFieldTemplateAdminPage,
  setIsPanelOpen,
  isPanelOpenSelector,
  fieldTemplateModelSelector,
  setFieldTemplateAttribute,
  saveFieldTemplate,
  fieldTemplateBeingUpdatedSelector,
  setFieldTemplateModel,
  fieldTemplateModelHasIdSelector,
  deleteFieldTemplate,
};

export default fieldTemplateAdministrationPageReducer;

const makeEmptyModel = (fieldId, hearingTypeId) => {
  const model = {
    type: FIELDTEMPLATE_TYPE,
    attributes: {},
    relationships: {
      [HEARINGTYPE_TYPE]: {
        data: {
          id: hearingTypeId,
          type: HEARINGTYPE_TYPE,
        },
      },
      [FIELD_TYPE]: {
        data: {
          id: fieldId,
          type: FIELD_TYPE,
        },
      },
    },
  };
  return model;
};
